import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Grid,
  CircularProgress,
} from '@mui/material'
import { Print, ArrowBack } from '@mui/icons-material'
import { Button, DocumentQRCode, PrintStyles } from '@components/common'
import { useDocumentRegistry } from '@hooks/useDocumentRegistry'
import { toast } from 'react-toastify'
import api from '@services/api'
import { useAuth } from '@context/AuthContext'
import { formatDate, formatDateTime } from '@utils/dateUtils'

const IssueVoucherPrint = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [voucher, setVoucher] = useState(null)
  const [loading, setLoading] = useState(true)
  const { registerDocument, verificationCode, loading: registering } = useDocumentRegistry()

  useEffect(() => {
    const fetchVoucher = async () => {
      try {
        const response = await api.get(`/inventory/issue-vouchers/${id}/`)
        setVoucher(response.data)
      } catch (error) {
        console.error('Error fetching voucher:', error)
        toast.error('Failed to load issue voucher')
        navigate('/inventory/issue-vouchers')
      } finally {
        setLoading(false)
      }
    }
    fetchVoucher()
  }, [id, navigate])

  // Set document title for meaningful PDF filename
  useEffect(() => {
    if (voucher) {
      document.title = `Issue Voucher - ${voucher.voucher_number} - UCI CMMS`
    }
    return () => {
      document.title = 'UCI CMMS'
    }
  }, [voucher])

  // Register document when voucher is loaded
  useEffect(() => {
    if (voucher && !verificationCode) {
      registerDocument('issue_voucher', voucher.voucher_number, parseInt(id), voucher)
        .catch((err) => console.error('Failed to register document:', err))
    }
  }, [voucher, id, verificationCode, registerDocument])

  const handlePrint = async () => {
    if (!verificationCode && voucher) {
      try {
        await registerDocument('issue_voucher', voucher.voucher_number, parseInt(id), voucher)
      } catch (err) {
        console.error('Registration failed:', err)
      }
    }
    window.print()
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!voucher) {
    return null
  }

  return (
    <>
      {/* Print Controls - Hidden when printing */}
      <Box className="print-controls no-print" sx={{ p: 2, display: 'flex', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          onClick={() => navigate('/inventory/issue-vouchers')}
        >
          Back
        </Button>
        <Button
          startIcon={<Print />}
          onClick={handlePrint}
          disabled={registering}
        >
          {registering ? 'Preparing...' : 'Print'}
        </Button>
      </Box>

      {/* Printable Content */}
      <Box className="print-content" sx={{ bgcolor: 'white' }}>
        {/* Header with UCI Logo */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {/* Logo centered on first line */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <img
              src="/images/uci-logo.png"
              alt="UCI Logo"
              style={{ height: '70px' }}
            />
          </Box>
          {/* System name prominently displayed */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: '#1B5E7A',
              letterSpacing: '0.5px',
              mb: 2
            }}
          >
            Computerized Maintenance Management System
          </Typography>
          {/* Document title */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', textDecoration: 'underline' }}>
            ISSUE VOUCHER
          </Typography>
        </Box>

        {/* Voucher Header Info */}
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Voucher Number</TableCell>
                <TableCell sx={{ width: '25%' }}>{voucher.voucher_number}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Date</TableCell>
                <TableCell sx={{ width: '25%' }}>
                  {formatDate(voucher.voucher_date) || '-'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>To Section/Unit</TableCell>
                <TableCell>{voucher.to_department_name || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Unit/Ward</TableCell>
                <TableCell>{voucher.unit_ward || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell colSpan={3}>{voucher.status_display || voucher.status}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Line Items */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          ITEMS ISSUED
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }} width={50}>No.</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Nature of Goods</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} width={80}>Folio</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center" width={80}>Qty</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} width={80}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right" width={120}>Unit Cost</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right" width={120}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {voucher.line_items && voucher.line_items.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    {item.nature_of_goods}
                    {item.spare_part_number && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Part #: {item.spare_part_number}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{item.folio || '-'}</TableCell>
                  <TableCell align="center">{item.quantity}</TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell align="right">
                    {item.unit_cost ? `UGX ${Number(item.unit_cost).toLocaleString()}` : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {item.total_amount ? `UGX ${Number(item.total_amount).toLocaleString()}` : '-'}
                  </TableCell>
                </TableRow>
              ))}
              {/* Empty rows for manual entry if needed */}
              {(!voucher.line_items || voucher.line_items.length < 5) && (
                [...Array(5 - (voucher.line_items?.length || 0))].map((_, idx) => (
                  <TableRow key={`empty-${idx}`}>
                    <TableCell>&nbsp;</TableCell>
                    <TableCell>&nbsp;</TableCell>
                    <TableCell>&nbsp;</TableCell>
                    <TableCell>&nbsp;</TableCell>
                    <TableCell>&nbsp;</TableCell>
                    <TableCell>&nbsp;</TableCell>
                    <TableCell>&nbsp;</TableCell>
                  </TableRow>
                ))
              )}
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell colSpan={6} align="right" sx={{ fontWeight: 'bold' }}>
                  GRAND TOTAL:
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  UGX {Number(voucher.total_amount || 0).toLocaleString()}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Notes Section */}
        {voucher.notes && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              NOTES
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="body2">{voucher.notes}</Typography>
            </Paper>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Signatures Section */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          SIGNATURES
        </Typography>

        <Grid container spacing={4}>
          {/* Issued By */}
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ mb: 1 }}><strong>Issued By:</strong></Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="body2">Sign:</Typography>
              <Box sx={{ borderBottom: '1px solid black', minWidth: 100, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {voucher.issued_by_signature ? (
                  <Box
                    component="img"
                    src={voucher.issued_by_signature}
                    sx={{ maxHeight: 28, objectFit: 'contain' }}
                  />
                ) : (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', fontWeight: 'bold' }}>
                    {voucher.issued_by_initials || ''}
                  </Typography>
                )}
              </Box>
            </Box>
            <Typography variant="body2">
              Name: {voucher.issued_by_name || '_______________'}
            </Typography>
            <Typography variant="body2">
              Title: {voucher.issued_by_title || voucher.issued_by_job_title || '_______________'}
            </Typography>
            <Typography variant="body2">
              Date: {formatDate(voucher.issued_at) || '_______________'}
            </Typography>
          </Grid>

          {/* Received By */}
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ mb: 1 }}><strong>Received By:</strong></Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="body2">Sign:</Typography>
              <Box sx={{ borderBottom: '1px solid black', minWidth: 100, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {voucher.received_by_signature ? (
                  <Box
                    component="img"
                    src={voucher.received_by_signature}
                    sx={{ maxHeight: 28, objectFit: 'contain' }}
                  />
                ) : (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', fontWeight: 'bold' }}>
                    {voucher.received_by_initials || ''}
                  </Typography>
                )}
              </Box>
            </Box>
            <Typography variant="body2">
              Name: {voucher.received_by_user_name || voucher.received_by_name || '_______________'}
            </Typography>
            <Typography variant="body2">
              Title: {voucher.received_by_title || voucher.received_by_job_title || '_______________'}
            </Typography>
            <Typography variant="body2">
              Date: {formatDate(voucher.received_at) || '_______________'}
            </Typography>
          </Grid>
        </Grid>

        {/* Copy Distribution (UCI Form requirement) */}
        <Box sx={{ mt: 4, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 1 }}>
            DISTRIBUTION:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption">
                [ ] Original - Accounts
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption">
                [ ] Duplicate - Recipient
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption">
                [ ] Triplicate - Stores
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption">
                [ ] Quadruplicate - File
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Printed: {formatDateTime(new Date())}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Printed by: {(user?.first_name || user?.last_name) ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim() : user?.username || 'N/A'}
            </Typography>
          </Box>
          <DocumentQRCode
            verificationCode={verificationCode}
            documentType="Issue Voucher"
            documentNumber={voucher?.voucher_number || ''}
            size={80}
          />
        </Box>
      </Box>

      <PrintStyles orientation="portrait" />
    </>
  )
}

export default IssueVoucherPrint
